import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Paperclip, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { adminInputClass } from '@/components/admin/AdminTableStyles';
import { accountingError, shortDate } from '@/lib/accounting';
import {
  getAccountingInvoiceEmail,
  previewAccountingInvoiceEmail,
  sendAccountingInvoiceEmail,
} from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Send — or resend — an invoice to its customer.
 *
 * The preview on the right is not a mock-up. It is the message rendered by the
 * same server function that will send it, from the same subject and body, so
 * what is on screen is what arrives. That is why it is fetched rather than
 * assembled here: a browser-side impression of the email would be a second
 * template, and two templates drift.
 *
 * It is debounced rather than fetched per keystroke, and the frame keeps the
 * last good render while the next one is in flight, so typing never flashes an
 * empty panel.
 *
 * The recipient comes from the customer record. There is no send without a
 * valid one — the server refuses it, and so does this button, because a
 * "Sent" toast for a message with nowhere to go is the worst outcome here.
 */

const PREVIEW_DEBOUNCE_MS = 400;

export default function InvoiceEmailDialog({ invoice, open, onOpenChange, onSent }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  const ref = invoice?.ref;

  useEffect(() => {
    if (!open || !ref) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setPreview('');
    (async () => {
      try {
        const { data } = await getAccountingInvoiceEmail(ref);
        if (cancelled) return;
        setState({
          sender: data.sender || '',
          to: data.to || '',
          subject: data.subject || '',
          message: data.message || '',
          attach_pdf: data.attach_pdf !== false,
          copy_to_self: false,
          senderAccounts: data.sender_accounts || [],
          selfEmail: data.self_email || '',
          kind: data.kind,
          actionLabel: data.action_label || 'Send invoice',
          lastSent: data.last_sent || null,
          issuesOnSend: Boolean(data.issues_on_send),
          customer: data.customer || {},
        });
      } catch (err) {
        if (!cancelled) setError(accountingError(err, 'The composer could not be opened.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, ref]);

  const renderPreview = useCallback(
    async (subject, message, attachPdf) => {
      if (!ref) return;
      const id = requestId.current + 1;
      requestId.current = id;
      setPreviewing(true);
      try {
        const { data } = await previewAccountingInvoiceEmail(ref, {
          subject,
          message,
          attach_pdf: attachPdf,
        });
        // A slower earlier request must not overwrite a newer render.
        if (requestId.current === id) setPreview(data.html || '');
      } catch {
        // The last good render stays on screen; sending is what must not lie.
      } finally {
        if (requestId.current === id) setPreviewing(false);
      }
    },
    [ref]
  );

  useEffect(() => {
    if (!open || !state) return undefined;
    const timer = setTimeout(
      () => renderPreview(state.subject, state.message, state.attach_pdf),
      preview ? PREVIEW_DEBOUNCE_MS : 0
    );
    return () => clearTimeout(timer);
    // `preview` is read only to decide whether the first render waits; it must
    // not itself re-trigger one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, state?.subject, state?.message, state?.attach_pdf, renderPreview]);

  const set = (patch) => setState((current) => ({ ...current, ...patch }));

  const send = async () => {
    setSending(true);
    try {
      const { data } = await sendAccountingInvoiceEmail(ref, {
        sender: state.sender,
        to: state.to,
        subject: state.subject,
        message: state.message,
        attach_pdf: state.attach_pdf,
        copy_to_self: state.copy_to_self,
      });
      onOpenChange(false);
      toast.success(`Invoice ${invoice.number} sent to ${data.to}.`);
      await onSent?.(data);
    } catch (err) {
      // A failed send says so and leaves the composer open with the wording
      // intact, so it can be retried rather than retyped.
      toast.error(accountingError(err, 'The email could not be sent.'));
    } finally {
      setSending(false);
    }
  };

  const canSend = Boolean(state?.to) && !sending && !loading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[94vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{state?.actionLabel || 'Send invoice'}</DialogTitle>
          <DialogDescription>
            {invoice && <>Invoice {invoice.number} · {invoice.customer?.name}</>}
            {state?.lastSent?.sent_at && (
              <span className="mt-1 block text-xs">
                Last sent {shortDate(state.lastSent.sent_at)} to {state.lastSent.to}
                {state.lastSent.by ? ` by ${state.lastSent.by}` : ''}.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : loading || !state ? (
          <div className="flex min-h-[240px] items-center justify-center text-sm text-[#062B1A]/50">
            <Loader2 className="mr-2 size-5 animate-spin text-[#D4AF37]" />
            Preparing the message…
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="space-y-3">
              <div>
                <Label htmlFor="email-from">From</Label>
                <select
                  id="email-from"
                  value={state.sender}
                  onChange={(event) => set({ sender: event.target.value })}
                  className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
                >
                  {state.senderAccounts.map((account) => (
                    <option key={account.email} value={account.email}>
                      {account.name} &lt;{account.email}&gt;
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="email-to">To</Label>
                <Input
                  id="email-to"
                  type="email"
                  value={state.to}
                  onChange={(event) => set({ to: event.target.value })}
                  placeholder="customer@example.com"
                  className={cn('mt-1', adminInputClass)}
                />
                {!state.to && (
                  <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-snug text-red-700">
                    <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                    {state.customer?.name || 'This customer'} has no email address on file. Add one
                    on the customer record, or type the recipient here.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="email-subject">Subject</Label>
                <Input
                  id="email-subject"
                  value={state.subject}
                  onChange={(event) => set({ subject: event.target.value })}
                  className={cn('mt-1', adminInputClass)}
                />
              </div>

              <div>
                <Label htmlFor="email-message">Message</Label>
                <Textarea
                  id="email-message"
                  rows={10}
                  value={state.message}
                  onChange={(event) => set({ message: event.target.value })}
                  className={cn('mt-1 resize-y', adminInputClass)}
                />
                <p className="mt-1 text-[11px] text-[#062B1A]/45">
                  Plain text. The invoice figures, the branding and the footer are added by the
                  template — they are not part of what you type.
                </p>
              </div>

              <div className="space-y-2 rounded-lg border border-[rgba(6,43,26,0.1)] bg-[#F7F8FA] px-3 py-2.5">
                <label className="flex items-start gap-2.5 text-sm text-[#062B1A]">
                  <Checkbox
                    checked={state.attach_pdf}
                    onCheckedChange={(checked) => set({ attach_pdf: checked === true })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="flex items-center gap-1.5 font-medium">
                      <Paperclip className="size-3.5" />
                      Attach the invoice as a PDF
                    </span>
                    <span className="mt-0.5 block text-[11px] text-[#062B1A]/50">
                      The current version of this invoice, generated when you send.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-sm text-[#062B1A]">
                  <Checkbox
                    checked={state.copy_to_self}
                    disabled={!state.selfEmail}
                    onCheckedChange={(checked) => set({ copy_to_self: checked === true })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">Send a copy to myself</span>
                    <span className="mt-0.5 block text-[11px] text-[#062B1A]/50">
                      {state.selfEmail
                        ? `Blind copy to ${state.selfEmail}.`
                        : 'Your account has no email address on file.'}
                    </span>
                  </span>
                </label>
              </div>

              {state.issuesOnSend && (
                <p className="rounded-lg border border-[#D4AF37]/40 bg-[#FBF7EA] px-3 py-2 text-xs text-[#062B1A]/75">
                  This invoice is still a draft. Sending it issues it, so it starts counting as
                  money owed.
                </p>
              )}
            </div>

            <div className="min-w-0">
              <div className="mb-1.5 flex items-center justify-between">
                <Label>Preview</Label>
                {previewing && (
                  <span className="flex items-center gap-1.5 text-[11px] text-[#062B1A]/45">
                    <Loader2 className="size-3 animate-spin" />
                    Updating
                  </span>
                )}
              </div>
              {/* Sandboxed with no allowances: the frame renders the message
                  and can do nothing else. */}
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={preview}
                className="h-[540px] w-full rounded-lg border border-[rgba(6,43,26,0.12)] bg-white"
              />
              <p className="mt-1.5 text-[11px] text-[#062B1A]/45">
                Rendered by the same code that sends the message.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={send}
            disabled={!canSend}
            title={state && !state.to ? 'There is no recipient for this invoice' : undefined}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {sending ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Send className="mr-2 size-4" />
            )}
            {state?.actionLabel || 'Send invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
